/* ==========================================================================
   STEALTHCALC — theme.js
   Vanilla JS. No build step, no dependencies. Every module below checks for
   the presence of its target elements before doing any work.
   ========================================================================== */
(function () {
  'use strict';

  var theme = window.theme || {};

  function motionEnabled() {
    return (
      !document.documentElement.classList.contains('no-motion') &&
      !window.matchMedia('(prefers-reduced-motion: reduce)').matches
    );
  }

  function finePointer() {
    return window.matchMedia('(pointer: fine)').matches;
  }

  /* ---------------------------------------------------------------------
     Money formatting (mirrors Shopify's `money_format` filter)
     ------------------------------------------------------------------- */
  function formatMoney(cents, format) {
    if (typeof cents === 'string') cents = cents.replace('.', '');
    format = format || (theme.moneyFormat || '${{amount}}');
    var value = '';
    var placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;

    function defaultTo(value, defaultValue) {
      return value == null || value !== value ? defaultValue : value;
    }
    function formatWithDelimiters(number, precision, thousands, decimal) {
      precision = defaultTo(precision, 2);
      thousands = defaultTo(thousands, ',');
      decimal = defaultTo(decimal, '.');
      if (isNaN(number) || number == null) return 0;
      number = (number / 100.0).toFixed(precision);
      var parts = number.split('.');
      var dollars = parts[0].replace(/(\d)(?=(\d{3})+(?!\d))/g, '$1' + thousands);
      var cents2 = parts[1] ? decimal + parts[1] : '';
      return dollars + cents2;
    }

    switch (format.match(placeholderRegex)[1]) {
      case 'amount':
        value = formatWithDelimiters(cents, 2);
        break;
      case 'amount_no_decimals':
        value = formatWithDelimiters(cents, 0);
        break;
      case 'amount_with_comma_separator':
        value = formatWithDelimiters(cents, 2, '.', ',');
        break;
      case 'amount_no_decimals_with_comma_separator':
        value = formatWithDelimiters(cents, 0, '.', ',');
        break;
      default:
        value = formatWithDelimiters(cents, 2);
    }
    return format.replace(placeholderRegex, value);
  }

  /* ---------------------------------------------------------------------
     Scroll reveal (IntersectionObserver)
     ------------------------------------------------------------------- */
  function initReveal() {
    var targets = document.querySelectorAll('[data-reveal], [data-stagger]');
    if (!targets.length || !('IntersectionObserver' in window)) {
      targets.forEach(function (el) { el.classList.add('is-visible'); });
      return;
    }
    var io = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible');
            io.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.05, rootMargin: '0px 0px -4% 0px' }
    );
    targets.forEach(function (el) { io.observe(el); });
  }

  /* ---------------------------------------------------------------------
     Hero scroll parallax — subtle 3D recede as the visitor scrolls away
     ------------------------------------------------------------------- */
  function initHeroParallax() {
    if (!motionEnabled()) return;
    var stage = document.querySelector('.hero__stage');
    if (!stage) return;
    stage.style.perspective = '1200px';
    var device = stage.querySelector('.device');
    if (!device) return;
    var ticking = false;
    function update() {
      ticking = false;
      var y = window.scrollY;
      if (y > window.innerHeight * 1.2) return;
      var p = Math.min(1, y / (window.innerHeight * 0.9));
      device.style.translate = '0 ' + (p * 46) + 'px';
      device.style.scale = String(1 - p * 0.06);
      device.style.opacity = String(1 - p * 0.35);
    }
    window.addEventListener('scroll', function () {
      if (!ticking) {
        ticking = true;
        requestAnimationFrame(update);
      }
    }, { passive: true });
  }

  /* ---------------------------------------------------------------------
     Spline 3D scene slot — lazy-loads the viewer only if a scene URL is
     configured in the Theme Editor, and only when near the viewport
     ------------------------------------------------------------------- */
  function initSpline() {
    var slots = document.querySelectorAll('[data-spline][data-spline-url]');
    if (!slots.length) return;
    var loaded = false;
    function mount(slot) {
      if (!loaded) {
        loaded = true;
        var s = document.createElement('script');
        s.type = 'module';
        s.src = 'https://unpkg.com/@splinetool/viewer@1.9.82/build/spline-viewer.js';
        document.head.appendChild(s);
      }
      var viewer = document.createElement('spline-viewer');
      viewer.setAttribute('url', slot.getAttribute('data-spline-url'));
      viewer.setAttribute('loading-anim-type', 'none');
      slot.appendChild(viewer);
    }
    if ('IntersectionObserver' in window) {
      var io = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            mount(entry.target);
            io.unobserve(entry.target);
          }
        });
      }, { rootMargin: '300px' });
      slots.forEach(function (el) { io.observe(el); });
    } else {
      slots.forEach(mount);
    }
  }

  /* ---------------------------------------------------------------------
     3D model hero — lazy-loads Google model-viewer only when a model slot
     is present and near the viewport. Auto-rotate respects reduced motion.
     ------------------------------------------------------------------- */
  function initModelViewer() {
    var slots = document.querySelectorAll('[data-model-viewer][data-model-src]');
    if (!slots.length) return;
    var loaded = false;
    function mount(slot) {
      if (!loaded) {
        loaded = true;
        var s = document.createElement('script');
        s.type = 'module';
        s.src = 'https://cdn.jsdelivr.net/npm/@google/model-viewer@3.5.0/dist/model-viewer.min.js';
        document.head.appendChild(s);
      }
      var viewer = document.createElement('model-viewer');
      viewer.setAttribute('src', slot.getAttribute('data-model-src'));
      viewer.setAttribute('interaction-prompt', 'none');
      viewer.setAttribute('shadow-intensity', '1');
      viewer.setAttribute('exposure', '1.1');
      viewer.setAttribute('loading', 'eager');
      viewer.setAttribute('camera-orbit', '270deg 78deg 105%');
      viewer.setAttribute('interpolation-decay', '160');
      slot.appendChild(viewer);

      // Poster crossfade: the branded product photo shows instantly and
      // hands off to the interactive model once it has actually rendered.
      viewer.addEventListener('load', function () {
        slot.classList.add('is-model-ready');
      });

      // Scroll-linked rotation: the model turns as the visitor scrolls,
      // instead of spinning on its own.
      if (motionEnabled()) {
        var ticking = false;
        function updateOrbit() {
          ticking = false;
          var vh = window.innerHeight;
          if (window.scrollY > vh * 2) return;
          var progress = Math.min(window.scrollY / vh, 1.5);
          var theta = 270 + progress * 150;
          viewer.setAttribute('camera-orbit', theta.toFixed(1) + 'deg 78deg 105%');
        }
        window.addEventListener('scroll', function () {
          if (!ticking) {
            ticking = true;
            requestAnimationFrame(updateOrbit);
          }
        }, { passive: true });
        updateOrbit();
      }
    }
    if ('IntersectionObserver' in window) {
      var io = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            mount(entry.target);
            io.unobserve(entry.target);
          }
        });
      }, { rootMargin: '400px' });
      slots.forEach(function (el) { io.observe(el); });
    } else {
      slots.forEach(mount);
    }
  }

  /* ---------------------------------------------------------------------
     Sparkles background — vanilla canvas port of the 21st.dev
     SparklesCore component (tsparticles slim behavior: drifting dots
     with per-particle opacity twinkle). Pauses offscreen; static under
     reduced motion.
     ------------------------------------------------------------------- */
  function initSparkles() {
    var canvases = document.querySelectorAll('canvas[data-sparkles]');
    if (!canvases.length) return;

    canvases.forEach(function (canvas) {
      var ctx = canvas.getContext('2d');
      if (!ctx) return;
      var color = canvas.getAttribute('data-sparkles-color') || '#FFFFFF';
      var host = canvas.parentElement;
      var dpr = Math.min(window.devicePixelRatio || 1, 2);
      var particles = [];

      function build() {
        canvas.width = host.clientWidth * dpr;
        canvas.height = host.clientHeight * dpr;
        var count = Math.min(300, Math.round((canvas.width * canvas.height) / (dpr * dpr * 1600)));
        particles = [];
        for (var i = 0; i < count; i++) {
          particles.push({
            x: Math.random() * canvas.width,
            y: Math.random() * canvas.height,
            r: (0.5 + Math.random()) * dpr,
            base: 0.12 + Math.random() * 0.75,
            phase: Math.random() * Math.PI * 2,
            tw: 0.4 + Math.random() * 1.2,
            vx: (Math.random() - 0.5) * 0.22 * dpr,
            vy: (Math.random() - 0.5) * 0.22 * dpr
          });
        }
      }
      build();
      if ('ResizeObserver' in window) new ResizeObserver(build).observe(host);

      function draw(t, animate) {
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        ctx.fillStyle = color;
        for (var i = 0; i < particles.length; i++) {
          var p = particles[i];
          if (animate) {
            p.x += p.vx; p.y += p.vy;
            if (p.x < 0) p.x = canvas.width; else if (p.x > canvas.width) p.x = 0;
            if (p.y < 0) p.y = canvas.height; else if (p.y > canvas.height) p.y = 0;
          }
          var op = animate ? p.base * (0.35 + 0.65 * (0.5 + 0.5 * Math.sin(t * 0.001 * p.tw + p.phase))) : p.base * 0.5;
          ctx.globalAlpha = op;
          ctx.beginPath();
          ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
          ctx.fill();
        }
        ctx.globalAlpha = 1;
      }

      if (!motionEnabled()) {
        draw(0, false);
        return;
      }

      var running = false;
      var raf = null;
      function loop(t) {
        if (!running) return;
        draw(t, true);
        raf = requestAnimationFrame(loop);
      }
      function start() { if (!running) { running = true; raf = requestAnimationFrame(loop); } }
      function stop() { running = false; if (raf) cancelAnimationFrame(raf); }
      if ('IntersectionObserver' in window) {
        new IntersectionObserver(function (entries) {
          entries.forEach(function (entry) { entry.isIntersecting ? start() : stop(); });
        }).observe(canvas);
      } else {
        start();
      }
    });
  }

  /* ---------------------------------------------------------------------
     Animated beams — vanilla port of the 21st.dev AnimatedBeam. Draws
     curved SVG paths from each model node to the device and runs a light
     pulse along them. Static faint paths under reduced motion.
     ------------------------------------------------------------------- */
  function initBeams() {
    var stages = document.querySelectorAll('[data-beams]');
    if (!stages.length) return;
    var SVGNS = 'http://www.w3.org/2000/svg';
    var accent = (getComputedStyle(document.documentElement).getPropertyValue('--accent') || '#3E8BFF').trim();

    stages.forEach(function (stage, stageIdx) {
      var svg = stage.querySelector('[data-beam-svg]');
      var center = stage.querySelector('[data-beam-center]');
      if (!svg || !center) return;
      var nodes = stage.querySelectorAll('[data-beam-node]');
      if (!nodes.length) return;
      var beams = [];

      function rel(el, rect) {
        var r = el.getBoundingClientRect();
        return { x: r.left - rect.left + r.width / 2, y: r.top - rect.top + r.height / 2 };
      }

      function build() {
        var rect = stage.getBoundingClientRect();
        if (rect.width < 600) { svg.innerHTML = ''; beams = []; return; }
        svg.setAttribute('viewBox', '0 0 ' + rect.width + ' ' + rect.height);
        svg.innerHTML = '';
        beams = [];
        var defs = document.createElementNS(SVGNS, 'defs');
        svg.appendChild(defs);
        var c = rel(center, rect);

        nodes.forEach(function (node, i) {
          var n = rel(node, rect);
          var curv = (i % 2 === 0 ? -1 : 1) * (26 + i * 9);
          var d = 'M ' + n.x + ',' + n.y + ' Q ' + (n.x + c.x) / 2 + ',' + ((n.y + c.y) / 2 + curv) + ' ' + c.x + ',' + c.y;

          var base = document.createElementNS(SVGNS, 'path');
          base.setAttribute('d', d);
          base.setAttribute('stroke', accent);
          base.setAttribute('stroke-opacity', '0.14');
          base.setAttribute('stroke-width', '1.5');
          base.setAttribute('stroke-linecap', 'round');
          base.setAttribute('fill', 'none');
          svg.appendChild(base);

          if (!motionEnabled()) return;

          var gradId = 'beam-' + stageIdx + '-' + i;
          var grad = document.createElementNS(SVGNS, 'linearGradient');
          grad.setAttribute('id', gradId);
          grad.setAttribute('gradientUnits', 'userSpaceOnUse');
          [['0%', accent, '0'], ['30%', accent, '1'], ['70%', '#FFFFFF', '0.9'], ['100%', '#FFFFFF', '0']].forEach(function (s) {
            var stop = document.createElementNS(SVGNS, 'stop');
            stop.setAttribute('offset', s[0]);
            stop.setAttribute('stop-color', s[1]);
            stop.setAttribute('stop-opacity', s[2]);
            grad.appendChild(stop);
          });
          defs.appendChild(grad);

          var pulse = document.createElementNS(SVGNS, 'path');
          pulse.setAttribute('d', d);
          pulse.setAttribute('stroke', 'url(#' + gradId + ')');
          pulse.setAttribute('stroke-width', '1.5');
          pulse.setAttribute('stroke-linecap', 'round');
          pulse.setAttribute('fill', 'none');
          svg.appendChild(pulse);

          beams.push({
            grad: grad,
            sx: n.x, sy: n.y, ex: c.x, ey: c.y,
            duration: 3800 + Math.random() * 2600,
            delay: i * 700
          });
        });
      }

      build();
      if ('ResizeObserver' in window) new ResizeObserver(build).observe(stage);
      if (!motionEnabled()) return;

      var running = false;
      var raf = null;
      function tick(now) {
        if (!running) return;
        for (var i = 0; i < beams.length; i++) {
          var b = beams[i];
          var t = ((now + b.delay) % b.duration) / b.duration;
          var span = 0.3;
          var head = t * (1 + span);
          var tail = head - span;
          b.grad.setAttribute('x1', String(b.sx + (b.ex - b.sx) * tail));
          b.grad.setAttribute('y1', String(b.sy + (b.ey - b.sy) * tail));
          b.grad.setAttribute('x2', String(b.sx + (b.ex - b.sx) * head));
          b.grad.setAttribute('y2', String(b.sy + (b.ey - b.sy) * head));
        }
        raf = requestAnimationFrame(tick);
      }
      function start() { if (!running) { running = true; raf = requestAnimationFrame(tick); } }
      function stop() { running = false; if (raf) cancelAnimationFrame(raf); }
      if ('IntersectionObserver' in window) {
        new IntersectionObserver(function (entries) {
          entries.forEach(function (entry) { entry.isIntersecting ? start() : stop(); });
        }).observe(stage);
      } else {
        start();
      }
    });
  }

  /* ---------------------------------------------------------------------
     Pointer spotlight — soft accent glow that follows the cursor in the
     hero (vanilla adaptation of the 21st.dev Spotlight component)
     ------------------------------------------------------------------- */
  function initSpotlight() {
    if (!finePointer() || !motionEnabled()) return;
    document.querySelectorAll('[data-spotlight-host]').forEach(function (host) {
      var spot = host.querySelector('[data-spotlight]');
      if (!spot) return;
      var raf = null;
      host.addEventListener('pointermove', function (e) {
        var rect = host.getBoundingClientRect();
        var x = e.clientX - rect.left;
        var y = e.clientY - rect.top;
        if (raf) cancelAnimationFrame(raf);
        raf = requestAnimationFrame(function () {
          spot.style.transform = 'translate(' + (x - 170) + 'px,' + (y - 170) + 'px)';
        });
      });
      host.addEventListener('pointerenter', function () { spot.classList.add('is-active'); });
      host.addEventListener('pointerleave', function () { spot.classList.remove('is-active'); });
    });
  }

  /* ---------------------------------------------------------------------
     Header scroll state
     ------------------------------------------------------------------- */
  function initHeader() {
    var header = document.querySelector('[data-header]');
    if (!header) return;
    function onScroll() {
      header.classList.toggle('is-scrolled', window.scrollY > 16);
    }
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
  }

  /* ---------------------------------------------------------------------
     Mobile menu
     ------------------------------------------------------------------- */
  function initMobileMenu() {
    var menu = document.querySelector('[data-mobile-menu]');
    var openBtn = document.querySelector('[data-menu-open]');
    if (!menu || !openBtn) return;

    function open() {
      menu.classList.add('is-open');
      menu.setAttribute('aria-hidden', 'false');
      openBtn.setAttribute('aria-expanded', 'true');
      document.body.style.overflow = 'hidden';
    }
    function close() {
      menu.classList.remove('is-open');
      menu.setAttribute('aria-hidden', 'true');
      openBtn.setAttribute('aria-expanded', 'false');
      document.body.style.overflow = '';
    }
    openBtn.addEventListener('click', open);
    menu.querySelectorAll('[data-menu-close]').forEach(function (el) {
      el.addEventListener('click', close);
    });
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape') close();
    });
  }

  /* ---------------------------------------------------------------------
     Announcement bar rotation
     ------------------------------------------------------------------- */
  function initAnnouncementBar() {
    var bar = document.querySelector('[data-announcement]');
    if (!bar) return;
    var msgs = bar.querySelectorAll('.announcement-bar__msg');
    var closeBtn = bar.querySelector('[data-announcement-close]');

    if (closeBtn) {
      closeBtn.addEventListener('click', function () {
        bar.style.display = 'none';
      });
    }

    if (msgs.length > 1 && motionEnabled()) {
      var i = 0;
      setInterval(function () {
        msgs[i].classList.remove('is-active');
        i = (i + 1) % msgs.length;
        msgs[i].classList.add('is-active');
      }, 4200);
    }
  }

  /* ---------------------------------------------------------------------
     Accordion (FAQ)
     ------------------------------------------------------------------- */
  function initAccordions() {
    document.querySelectorAll('[data-accordion]').forEach(function (accordion) {
      var items = accordion.querySelectorAll('.accordion-item');
      items.forEach(function (item) {
        var trigger = item.querySelector('.accordion-trigger');
        if (!trigger) return;
        trigger.addEventListener('click', function () {
          var isOpen = item.classList.contains('is-open');
          items.forEach(function (other) {
            other.classList.remove('is-open');
            var t = other.querySelector('.accordion-trigger');
            if (t) t.setAttribute('aria-expanded', 'false');
          });
          if (!isOpen) {
            item.classList.add('is-open');
            trigger.setAttribute('aria-expanded', 'true');
          }
        });
      });
    });
  }

  /* ---------------------------------------------------------------------
     Marquee pause toggle
     ------------------------------------------------------------------- */
  function initMarquee() {
    document.querySelectorAll('[data-marquee]').forEach(function (marquee) {
      var btn = marquee.querySelector('[data-marquee-toggle]');
      if (!btn) return;
      btn.addEventListener('click', function () {
        var paused = marquee.classList.toggle('is-paused');
        btn.setAttribute('aria-pressed', String(paused));
        btn.setAttribute('aria-label', paused ? 'Resume scrolling text' : 'Pause scrolling text');
      });
    });
  }

  /* ---------------------------------------------------------------------
     Capture demo crossfade (camera feature)
     ------------------------------------------------------------------- */
  function initCaptureDemo() {
    document.querySelectorAll('[data-capture-demo]').forEach(function (demo) {
      var panels = demo.querySelectorAll('.capture-panel');
      if (panels.length < 2 || !motionEnabled()) return;
      var i = 0;
      var interval = null;
      function tick() {
        panels[i].classList.remove('is-active');
        i = (i + 1) % panels.length;
        panels[i].classList.add('is-active');
      }
      if ('IntersectionObserver' in window) {
        new IntersectionObserver(function (entries) {
          entries.forEach(function (entry) {
            if (entry.isIntersecting && !interval) {
              interval = setInterval(tick, 2600);
            } else if (!entry.isIntersecting && interval) {
              clearInterval(interval);
              interval = null;
            }
          });
        }, { threshold: 0.4 }).observe(demo);
      } else {
        interval = setInterval(tick, 2600);
      }
    });
  }

  /* ---------------------------------------------------------------------
     Chat typewriter (AI feature)
     ------------------------------------------------------------------- */
  function initTypewriter() {
    var els = document.querySelectorAll('[data-typewriter]');
    if (!els.length || !('IntersectionObserver' in window)) return;
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        var el = entry.target;
        var text = el.getAttribute('data-text') || '';
        io.unobserve(el);
        if (!motionEnabled()) {
          el.textContent = text;
          el.classList.add('is-done');
          return;
        }
        var i = 0;
        el.textContent = '';
        var interval = setInterval(function () {
          i += 2;
          el.textContent = text.slice(0, i);
          if (i >= text.length) {
            clearInterval(interval);
            el.classList.add('is-done');
          }
        }, 12);
      });
    }, { threshold: 0.5 });
    els.forEach(function (el) { io.observe(el); });
  }

  /* ---------------------------------------------------------------------
     Magnetic buttons + device pointer tilt
     ------------------------------------------------------------------- */
  function initMagnetic() {
    if (!finePointer() || !motionEnabled()) return;
    document.querySelectorAll('[data-magnetic]').forEach(function (btn) {
      btn.addEventListener('pointermove', function (e) {
        var rect = btn.getBoundingClientRect();
        var x = e.clientX - rect.left - rect.width / 2;
        var y = e.clientY - rect.top - rect.height / 2;
        btn.style.transform = 'translate(' + x * 0.18 + 'px,' + y * 0.28 + 'px)';
      });
      btn.addEventListener('pointerleave', function () {
        btn.style.transform = '';
      });
    });
  }

  function initDeviceTilt() {
    if (!finePointer() || !motionEnabled()) return;
    document.querySelectorAll('[data-tilt]').forEach(function (stage) {
      var svg = stage.querySelector('.device__svg') || stage.querySelector('img');
      if (!svg) return;
      var raf = null;
      stage.addEventListener('pointermove', function (e) {
        var rect = stage.getBoundingClientRect();
        var px = (e.clientX - rect.left) / rect.width - 0.5;
        var py = (e.clientY - rect.top) / rect.height - 0.5;
        if (raf) cancelAnimationFrame(raf);
        raf = requestAnimationFrame(function () {
          svg.style.transform = 'rotateY(' + px * 10 + 'deg) rotateX(' + py * -10 + 'deg)';
        });
      });
      stage.addEventListener('pointerleave', function () {
        if (raf) cancelAnimationFrame(raf);
        svg.style.transform = '';
      });
    });
  }

  /* ---------------------------------------------------------------------
     Quantity steppers (cart drawer + product form)
     ------------------------------------------------------------------- */
  function initQuantitySteppers() {
    // Product form stepper (single input, not tied to a cart line)
    document.querySelectorAll('[data-qty-input]').forEach(function (input) {
      var wrap = input.closest('.qty-stepper');
      if (!wrap) return;
      var dec = wrap.querySelector('[data-qty-decrease-form]');
      var inc = wrap.querySelector('[data-qty-increase-form]');
      if (dec) dec.addEventListener('click', function () {
        input.value = Math.max(1, (parseInt(input.value, 10) || 1) - 1);
      });
      if (inc) inc.addEventListener('click', function () {
        input.value = (parseInt(input.value, 10) || 1) + 1;
      });
    });
  }

  /* ---------------------------------------------------------------------
     Cart drawer (open/close + AJAX add/update/remove)
     ------------------------------------------------------------------- */
  var Cart = (function () {
    function drawer() { return document.getElementById('cart-drawer'); }

    function open() {
      var d = drawer();
      if (!d) return;
      d.classList.add('is-open');
      d.setAttribute('aria-hidden', 'false');
      document.body.style.overflow = 'hidden';
    }
    function close() {
      var d = drawer();
      if (!d) return;
      d.classList.remove('is-open');
      d.setAttribute('aria-hidden', 'true');
      document.body.style.overflow = '';
    }

    function updateCountBubbles(count) {
      document.querySelectorAll('[data-cart-count]').forEach(function (el) {
        el.textContent = count;
        el.hidden = count === 0;
      });
    }

    function refreshDrawer(thenOpen) {
      fetch('/?sections=cart-drawer')
        .then(function (res) { return res.json(); })
        .then(function (data) {
          var html = data['cart-drawer'];
          var parser = new DOMParser();
          var doc = parser.parseFromString(html, 'text/html');
          var fresh = doc.getElementById('cart-drawer');
          var current = drawer();
          if (fresh && current) {
            current.replaceWith(fresh);
            bindDrawerEvents();
            if (thenOpen) open();
          }
          return fetch('/cart.js');
        })
        .then(function (res) { return res.json(); })
        .then(function (cart) { updateCountBubbles(cart.item_count); })
        .catch(function () {});
    }

    function addItem(form, submitBtn) {
      var textEl = submitBtn ? submitBtn.querySelector('[data-add-to-cart-text]') : null;
      var originalText = textEl ? textEl.textContent : null;
      var errorEl = form.querySelector('[data-cart-error]');
      if (errorEl) { errorEl.hidden = true; errorEl.textContent = ''; }
      if (submitBtn) submitBtn.disabled = true;
      if (textEl) textEl.textContent = theme.strings && theme.strings.adding ? theme.strings.adding : 'Adding…';

      fetch(theme.routes.cartAdd || '/cart/add.js', {
        method: 'POST',
        headers: { Accept: 'application/json' },
        body: new FormData(form)
      })
        .then(function (res) { return res.json(); })
        .then(function (data) {
          if (data.status) {
            if (errorEl) { errorEl.textContent = data.description || 'That item could not be added to your cart.'; errorEl.hidden = false; }
            if (textEl) textEl.textContent = originalText;
            if (submitBtn) submitBtn.disabled = false;
            return;
          }
          refreshDrawer(true);
          if (textEl) textEl.textContent = originalText;
          if (submitBtn) submitBtn.disabled = false;
        })
        .catch(function () {
          if (errorEl) { errorEl.textContent = 'Something went wrong. Please try again.'; errorEl.hidden = false; }
          if (textEl) textEl.textContent = originalText;
          if (submitBtn) submitBtn.disabled = false;
        });
    }

    function changeLine(line, quantity) {
      fetch(theme.routes.cartChange || '/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ line: line, quantity: quantity })
      }).then(function () { refreshDrawer(false); });
    }

    function bindDrawerEvents() {
      var d = drawer();
      if (!d) return;
      d.querySelectorAll('[data-cart-close]').forEach(function (el) {
        el.addEventListener('click', close);
      });
      d.addEventListener('click', function (e) {
        var incBtn = e.target.closest('[data-qty-increase]');
        var decBtn = e.target.closest('[data-qty-decrease]');
        var removeBtn = e.target.closest('[data-cart-remove]');
        var item = e.target.closest('[data-cart-item]');
        if (!item) return;
        var line = item.getAttribute('data-line');
        var valueEl = item.querySelector('.qty-stepper__value');
        var current = valueEl ? parseInt(valueEl.textContent, 10) : 1;
        if (incBtn) changeLine(line, current + 1);
        if (decBtn) changeLine(line, Math.max(0, current - 1));
        if (removeBtn) changeLine(line, 0);
      });
    }

    document.addEventListener('click', function (e) {
      if (e.target.closest('[data-cart-open]')) {
        e.preventDefault();
        open();
      }
    });
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape') close();
    });

    bindDrawerEvents();

    return { open: open, close: close, addItem: addItem, refreshDrawer: refreshDrawer };
  })();

  function initProductForms() {
    document.querySelectorAll('[data-product-form]').forEach(function (form) {
      form.addEventListener('submit', function (e) {
        e.preventDefault();
        var submitBtn = form.querySelector('[data-add-to-cart]');
        Cart.addItem(form, submitBtn);
      });
    });
  }

  /* ---------------------------------------------------------------------
     Buy Now — adds the selected variant, then hands off to Shopify's
     native checkout. Never bypasses Shopify: /checkout owns payment,
     taxes, shipping, and inventory from here.
     ------------------------------------------------------------------- */
  function initBuyNow() {
    document.querySelectorAll('[data-product-form]').forEach(function (form) {
      var btn = form.querySelector('[data-buy-now]');
      if (!btn) return;
      var textEl = btn.querySelector('[data-buy-now-text]');
      var busy = false;

      btn.addEventListener('click', function () {
        if (busy || btn.disabled) return;
        busy = true;
        var originalText = textEl ? textEl.textContent : null;
        var errorEl = form.querySelector('[data-cart-error]');
        if (errorEl) { errorEl.hidden = true; errorEl.textContent = ''; }
        btn.setAttribute('aria-busy', 'true');
        btn.classList.add('is-loading');
        if (textEl) textEl.textContent = theme.strings && theme.strings.adding ? theme.strings.adding : 'Loading…';

        function fail(message) {
          busy = false;
          btn.removeAttribute('aria-busy');
          btn.classList.remove('is-loading');
          if (textEl) textEl.textContent = originalText;
          if (errorEl) { errorEl.textContent = message; errorEl.hidden = false; }
        }

        fetch(theme.routes.cartAdd || '/cart/add.js', {
          method: 'POST',
          headers: { Accept: 'application/json' },
          body: new FormData(form)
        })
          .then(function (res) { return res.json(); })
          .then(function (data) {
            if (data.status) {
              fail(data.description || 'That item could not be added to your cart.');
              return;
            }
            window.location.href = '/checkout';
          })
          .catch(function () {
            fail('Something went wrong. Please try again.');
          });
      });
    });
  }

  /* ---------------------------------------------------------------------
     Variant picker: sync radios -> hidden select -> price/media/stock
     ------------------------------------------------------------------- */
  function initVariantPickers() {
    document.querySelectorAll('[data-product-form]').forEach(function (form) {
      var jsonEl = form.querySelector('[data-product-json]');
      if (!jsonEl) return;
      var product;
      try { product = JSON.parse(jsonEl.textContent); } catch (err) { return; }

      var select = form.querySelector('[data-variant-select]');
      var fieldsets = form.querySelectorAll('.variant-picker');
      var section = form.closest('section');

      function currentOptions() {
        var opts = [];
        fieldsets.forEach(function (fs) {
          var checked = fs.querySelector('[data-option-value]:checked');
          opts.push(checked ? checked.value : null);
        });
        return opts;
      }

      function findVariant(opts) {
        return product.variants.find(function (v) {
          return [v.option1, v.option2, v.option3].every(function (val, i) {
            return opts[i] == null || val === opts[i];
          });
        });
      }

      function updateUI(variant) {
        if (!variant) return;
        if (select) select.value = variant.id;

        var priceEls = section ? section.querySelectorAll('.price') : [];
        priceEls.forEach(function (priceEl) {
          var currentEl = priceEl.querySelector('.price__current');
          if (currentEl) currentEl.textContent = formatMoney(variant.price);
          var compareEl = priceEl.querySelector('.price__compare');
          if (variant.compare_at_price && variant.compare_at_price > variant.price) {
            if (compareEl) {
              compareEl.textContent = formatMoney(variant.compare_at_price);
              compareEl.style.display = '';
            }
          } else if (compareEl) {
            compareEl.style.display = 'none';
          }
        });

        var stockEl = form.querySelector('[data-stock-status]');
        var stockCheckIcon = '<svg class="icon" viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M4 12.5l5 5L20 6" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/></svg>';
        if (stockEl) {
          stockEl.innerHTML = variant.available
            ? stockCheckIcon + ' ' + (theme.strings.inStock || 'In stock')
            : (theme.strings.outOfStock || 'Out of stock');
        }

        var addBtn = form.querySelector('[data-add-to-cart]');
        if (addBtn) {
          addBtn.disabled = !variant.available;
          var textEl = addBtn.querySelector('[data-add-to-cart-text]');
          if (textEl) textEl.textContent = variant.available ? (theme.strings.addToCart || 'Add to Cart') : (theme.strings.soldOut || 'Sold Out');
        }

        var buyNowBtn = form.querySelector('[data-buy-now]');
        if (buyNowBtn) buyNowBtn.disabled = !variant.available;

        if (section) {
          var stickyBtn = document.querySelector('[data-sticky-atc-submit]');
          var stickyPrice = document.querySelector('.sticky-atc .price__current');
          if (stickyPrice) stickyPrice.textContent = formatMoney(variant.price);
          if (stickyBtn) stickyBtn.disabled = !variant.available;

          if (variant.featured_media) {
            var slide = section.querySelector('[data-gallery-slide][data-media-id="' + variant.featured_media.id + '"]');
            var thumb = section.querySelector('[data-gallery-thumb][data-media-id="' + variant.featured_media.id + '"]');
            if (slide) {
              section.querySelectorAll('[data-gallery-slide]').forEach(function (s) { s.classList.remove('is-active'); });
              slide.classList.add('is-active');
            }
            if (thumb) {
              section.querySelectorAll('[data-gallery-thumb]').forEach(function (t) { t.classList.remove('is-active'); t.setAttribute('aria-selected', 'false'); });
              thumb.classList.add('is-active');
              thumb.setAttribute('aria-selected', 'true');
            }
          }
        }
      }

      fieldsets.forEach(function (fs) {
        fs.addEventListener('change', function () {
          var variant = findVariant(currentOptions());
          updateUI(variant);
        });
      });
    });
  }

  /* ---------------------------------------------------------------------
     Product gallery thumbnails
     ------------------------------------------------------------------- */
  function initGalleries() {
    document.querySelectorAll('[data-product-gallery]').forEach(function (gallery) {
      gallery.querySelectorAll('[data-gallery-thumb]').forEach(function (thumb) {
        thumb.addEventListener('click', function () {
          var id = thumb.getAttribute('data-media-id');
          gallery.querySelectorAll('[data-gallery-slide]').forEach(function (s) {
            s.classList.toggle('is-active', s.getAttribute('data-media-id') === id);
          });
          gallery.querySelectorAll('[data-gallery-thumb]').forEach(function (t) {
            t.classList.toggle('is-active', t === thumb);
            t.setAttribute('aria-selected', t === thumb ? 'true' : 'false');
          });
        });
      });
    });
  }

  /* ---------------------------------------------------------------------
     Sticky mobile add-to-cart bar
     ------------------------------------------------------------------- */
  function initStickyAtc() {
    var bar = document.querySelector('[data-sticky-atc]');
    var trigger = document.querySelector('[data-add-to-cart]');
    if (!bar || !trigger || !('IntersectionObserver' in window)) return;

    new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        bar.classList.toggle('is-visible', !entry.isIntersecting && entry.boundingClientRect.top < 0);
      });
    }, { threshold: 0 }).observe(trigger);

    var submitBtn = bar.querySelector('[data-sticky-atc-submit]');
    if (submitBtn) {
      submitBtn.addEventListener('click', function () {
        trigger.click();
        trigger.scrollIntoView({ behavior: motionEnabled() ? 'smooth' : 'auto', block: 'center' });
      });
    }
  }

  /* ---------------------------------------------------------------------
     Init
     ------------------------------------------------------------------- */
  document.addEventListener('DOMContentLoaded', function () {
    initReveal();
    initHeroParallax();
    initSpline();
    initModelViewer();
    initSparkles();
    initBeams();
    initSpotlight();
    initHeader();
    initMobileMenu();
    initAnnouncementBar();
    initAccordions();
    initMarquee();
    initCaptureDemo();
    initTypewriter();
    initMagnetic();
    initDeviceTilt();
    initQuantitySteppers();
    initProductForms();
    initBuyNow();
    initVariantPickers();
    initGalleries();
    initStickyAtc();

    var cartCount = document.querySelector('[data-cart-count]');
    if (cartCount) {
      fetch('/cart.js').then(function (r) { return r.json(); }).then(function (cart) {
        cartCount.textContent = cart.item_count;
        cartCount.hidden = cart.item_count === 0;
      }).catch(function () {});
    }
  });
})();
